import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import containsOrIsNode from '@cotyorg/ccx-utils/js/helpers/containsOrIsNode';
import isServer from '@cotyorg/ccx-utils/js/helpers/isServer';
import { OPEN_START, OPEN_END, CLOSE_START, CLOSE_END } from '../../modules/status';
import ModalKeyListener from '../ModalKeyListener';
import ModalCloseButton from '../ModalCloseButton';

const transitionMap = {
  fade: 'opacity',
  slide: 'transform',
};

export const generateModalClassNames = ({ status, transition }) => (
  classNames(
    'modal',
    `modal--${transition}-transition`, {
      'modal--before-open': status === OPEN_START,
      'modal--after-open': status === OPEN_END,
      'modal--before-close': status === CLOSE_START,
    },
  )
);

export const generateInnerClassNames = ({ hasGrid, modalContentInnerExtraClass }) => (
  classNames(
    'modal__content-inner',
    `${modalContentInnerExtraClass}`, {
      'grid-container': hasGrid,
    },
  )
);

export const isModalOpen = status => status === OPEN_START || status === OPEN_END;

export const getAriaProps = (aria = {}) => (
  Object
    .keys(aria)
    .map(key => ({ [`aria-${key}`]: aria[key] }))
    .reduce((acc, obj) => ({
      ...acc,
      ...obj,
    }), {})
);

export const getOnTransitionEndHandler = ({
  setStatus,
  id,
  status,
  transition,
}) => ({ propertyName }) => {
  if (status === CLOSE_START && propertyName === transitionMap[transition]) {
    setStatus({ id, status: CLOSE_END });
  }
};

// Need to use class in order to use lifecycle methods
// to bind and unbind document events
// istanbul ignore next
class ModalPortal extends PureComponent {
  constructor(props) {
    super(props);

    const { appElementSelector } = props;

    if (!isServer) {
      this.appElement = document.querySelector(appElementSelector);
    }
  }

  componentWillMount() {
    const { id, setStatus } = this.props;

    if (!isServer) {
      this.portalContainer = document.createElement('div');
      document.body.appendChild(this.portalContainer);
    }

    setStatus({ id, status: CLOSE_END });
  }

  componentDidUpdate() {
    const { setStatus, id, status } = this.props;
    const isOpen = isModalOpen(status);

    if (status === OPEN_START) {
      setStatus({ id, status: OPEN_END });
      this.wrapper.focus();
    }

    if (this.appElement && isOpen) {
      document.addEventListener('click', this.onDocumentClick, false);
      this.appElement.setAttribute('aria-hidden', 'true');
    } else if (this.appElement && !isOpen) {
      document.removeEventListener('click', this.onDocumentClick, false);
      this.appElement.removeAttribute('aria-hidden');
    }
  }

  componentWillUnmount() {
    if (!isServer) {
      document.removeEventListener('click', this.onDocumentClick, false);
      document.body.removeChild(this.portalContainer);
    }

    if (this.appElement) {
      this.appElement.removeAttribute('aria-hidden');
    }
  }

  onDocumentClick = ({ target }) => {
    const { status, setStatus, id } = this.props;
    const shouldClose = (
      isModalOpen(status) &&
      document.body.contains(target) &&
      !containsOrIsNode(this.contentInnerEl, target)
    );

    if (shouldClose) {
      setStatus({ id, status: CLOSE_START });
    }
  }

  render() {
    const {
      children,
      status,
      id,
      setStatus,
      closeIconSrc,
      contentLabel,
      aria = {},
      transition = 'fade',
      showCloseButton = true,
      modalContentExtraClass = '',
      modalContentInnerExtraClass = '',
      hasGrid = true,
    } = this.props;

    const isOpen = isModalOpen(status);
    const onTransitionEnd = getOnTransitionEndHandler({
      setStatus,
      id,
      status,
      transition,
    });

    if (isServer) {
      return null;
    }

    return ReactDOM.createPortal(
      (
        <div
          aria-modal="true"
          aria-hidden={!isOpen}
          aria-label={contentLabel}
          tabIndex="-1"
          className={generateModalClassNames({ status, transition })}
          onTransitionEnd={onTransitionEnd}
          ref={(el) => { this.wrapper = el; }}
          {...getAriaProps(aria)}
        >
          <ModalKeyListener
            isOpen={isOpen}
            wrapper={this.portalContainer}
            setStatus={setStatus}
            id={id}
          />
          <div className={`modal__content ${modalContentExtraClass}`}>
            <div
              ref={(el) => { this.contentInnerEl = el; }}
              className={generateInnerClassNames({ hasGrid, modalContentInnerExtraClass })}
            >
              { status !== CLOSE_END && children }
            </div>
          </div>
          { showCloseButton && (
            <ModalCloseButton
              closeIconSrc={closeIconSrc}
              setStatus={setStatus}
              id={id}
            />
          )}
        </div>
      ),
      this.portalContainer,
    );
  }
}

ModalPortal.propTypes = {
  children: PropTypes.node.isRequired,
  status: PropTypes.string,
  id: PropTypes.string.isRequired,
  setStatus: PropTypes.func.isRequired,
  closeIconSrc: PropTypes.string,
  contentLabel: PropTypes.string,
  aria: PropTypes.objectOf(PropTypes.string),
};

export default ModalPortal;
